/**
 * Patient Case — field-level effects.
 *
 * Migrated from: patient/patient-case-base.coffee
 *
 * Old validators → new effects:
 *   CheckMedication       → CheckMedication
 *   ChangeMedicalRetail   → ChangeMedicalRetail
 *   CheckBlacklistedDrugs → CheckBlacklistedDrugs
 *   ChangeCaseStatus      → ChangeCaseStatus
 *   AssignCurrentYear     → AssignCurrentYear
 *   AssignNextYear        → AssignNextYear
 *   CheckCondition        → CaseCheckCondition
 *   CheckForAlert         → CaseCheckForAlert
 *   MissingPhySignDate    → MissingPhySignDate
 *
 * Skipped:
 *   handle(dfd) init logic (check_stage) — form-load side-effects, not field-change
 *   update_patient_programs_statuses — save-time logic, belongs in Tier 3 if needed
 *   get_similar_inventory_items — internal helper, not a registered validator
 */
import dayjs from "dayjs";

import { buildApiUrl, fetchAPI } from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

// ─── CheckMedication ────────────────────────────────────────────────────────
// Old: DSLFx.Validators.CheckMedication
// Trigger field: medication_id
// Looks up the inventory item and drug_brand_short, then populates
// medication_short_name, copay/pap/cap availability, funding info, and helpful links.
export const CheckMedication: FieldHandler = (
	field,
	{ form, formMode, editID }
) => {
	void (async () => {
		const medicationId = field.value?.id;
		if (!medicationId) return;
		form.setFieldState("show_ndc_verify", (s) => {
			s.value = "No";
		});
		form.setFieldState("medication_id_breakdown", (s) => {
			s.value = "";
		});
		form.setFieldState("medication_short_name", (s) => {
			s.value = "";
		});
		form.setFieldState("funding_available", (s) => {
			s.value = "";
		});
		form.setFieldState("cap_available", (s) => {
			s.value = "";
		});
		form.setFieldState("cap_max", (s) => {
			s.value = "";
		});
		form.setFieldState("pap_available", (s) => {
			s.value = "";
		});

		try {
			const invData = await fetchAPI(
				buildApiUrl(`/inventory/${medicationId}`)
			);
			const inv = Array.isArray(invData) ? invData[0] : invData;
			if (!inv?.brand_name_id?.name) {
				field.setFeedback({
					type: "error",
					messages: [
						"Cannot find info for inventory item. Please try again.",
					],
				});
				return;
			}

			const brandName = inv.brand_name_id?.name;
			const noCapOverrides = [
				"epclusa",
				"harvoni",
				"mavyret",
				"vosevi",
				"zepatier",
			];
			if (noCapOverrides.includes(brandName.toLowerCase().trim())) {
				form.setFieldState("show_med_warning", (s) => {
					s.value = "Yes";
				});
			} else {
				form.setFieldState("show_med_warning", (s) => {
					s.value = "No";
				});
			}

			form.setFieldState("medication_short_name", (s) => {
				s.value = brandName;
			});

			const brandData = await fetchAPI(
				buildApiUrl(`/drug_brand_short/${inv.brand_name_id?.id}`)
			);
			const brand = Array.isArray(brandData) ? brandData[0] : brandData;
			if (!brand) {
				field.setFeedback({
					type: "error",
					messages: [
						"Cannot find info for short brand name. Please try again.",
					],
				});
				return;
			}

			form.setFieldState("cap_available", (s) => {
				s.value = brand.cap_available;
			});
			form.setFieldState("cap_max", (s) => {
				s.value = brand.cap_max;
			});
			form.setFieldState("pap_available", (s) => {
				s.value = brand.pap_available;
			});
			form.setFieldState("funding_available", (s) => {
				s.value = brand.funding_available;
			});

			const isCopayFound = brand.copay_available || "No";
			const is340bFound = brand.discount_drug_program_available || "No";
			form.setFieldState("is_copay_available", (s) => {
				s.value = isCopayFound;
			});
			form.setFieldState("is_340b_available", (s) => {
				s.value = is340bFound;
			});

			//COMPLICATED-CHECK-FUNCTIONALITY
			// Old system rebuilt a Select2 dropdown for copay_program_case from
			// drug_copay/drug_copay_auto_name arrays. In the new system, set dataSource.
			if (brand.drug_copay) {
				const copayOptions = brand.drug_copay.map((copay: any) => ({
					value: copay.id,
					label: brand.name,
				}));
				form.setFieldState("copay_program_case", (s) => {
					s.dataSource = copayOptions;
				});
			}

			if (brand.helpful_links && brand.helpful_links.length > 0) {
				form.setFieldState("helpful_links", (s) => {
					s.value = {
						url: brand.helpful_links,
						title: brand.helpful_links,
					};
				});
			} else {
				form.setFieldState("helpful_links", (s) => {
					s.value = "";
				});
			}

			field.setFeedback({ type: "error", messages: [] });
		} catch (err) {
			console.error("CheckMedication: API error", err);
			field.setFeedback({
				type: "error",
				messages: [
					"Cannot find info for inventory item. Please try again.",
				],
			});
		}
	})();
	return "";
};

// ─── ChangeMedicalRetail ────────────────────────────────────────────────────
// Old: DSLFx.Validators.ChangeMedicalRetail
// Trigger field: medical_retail
// Validates that the selected medical_retail type is supported by the patient's plan.
export const ChangeMedicalRetail: FieldHandler = (field, { form }) => {
	void (async () => {
		const medicalRetail = field.value;
		if (!medicalRetail) return;

		const planId = form.getFieldState("plan_id")?.value?.id;
		if (!planId) return;

		try {
			const data = await fetchAPI(
				buildApiUrl(`/plan/${parseInt(planId)}`)
			);
			const plan = Array.isArray(data) ? data[0] : data;
			if (
				plan?.medical_retail &&
				!plan.medical_retail.includes(medicalRetail)
			) {
				field.setFeedback({
					type: "error",
					messages: [
						`Patient's plan ${plan.name} doesn't support ${medicalRetail} type case`,
					],
				});
				form.setFieldState("medical_retail", (s) => {
					s.value = "";
				});
			} else {
				field.setFeedback({ type: "error", messages: [] });
			}
		} catch (err) {
			console.error("ChangeMedicalRetail: API error", err);
		}
	})();
	return "";
};

// ─── CheckBlacklistedDrugs ──────────────────────────────────────────────────
// Old: DSLFx.Validators.CheckBlacklistedDrugs
// Trigger field: medication_id
// Checks if the selected drug/medication is marked as unbillable for the patient's plan.
export const CheckBlacklistedDrugs: FieldHandler = (field, { form }) => {
	void (async () => {
		const medicationId = field.value?.id;
		if (!medicationId) return;

		const planID = form.getFieldState("plan_id")?.value?.id;
		if (!planID) return;

		try {
			const data = await fetchAPI(
				buildApiUrl(
					`/query/plan_unbillable_formulary/list?p_plan_id=${planID}&p_medication_id=${medicationId}`
				)
			);
			if (data?.data.length > 0) {
				field.setFeedback({
					type: "warning",
					messages: [
						"Selected drug/medication short name is marked as Unbillable Formulary in the Plan.",
					],
				});
			} else {
				field.setFeedback({ type: "warning", messages: [] });
			}
		} catch (err) {
			console.error("CheckBlacklistedDrugs: API error", err);
		}
	})();
	return "";
};

// ─── ChangeCaseStatus ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.ChangeCaseStatus
// Trigger field: status (or medication_short_name)
// Checks for duplicate cases and sets require_next_fill based on existing dispenses.
export const ChangeCaseStatus: FieldHandler = (
	field,
	{ form, formMode, editID }
) => {
	void (async () => {
		const patientId = form.getFieldState("patient_id")?.value?.id;
		const medName = form.getFieldState("medication_short_name")?.value;

		if (!medName) {
			field.setFeedback({ type: "error", messages: [] });
			return;
		}

		// Check for duplicate case (only in create mode)
		if (!editID) {
			try {
				const dupData = await fetchAPI(
					buildApiUrl(
						`/patient_case/list?_patient_id=eq:${patientId}&_medication_short_name=eq:${encodeURIComponent(medName)}`
					)
				);
				const dupRows = Array.isArray(dupData)
					? dupData
					: (dupData?.data ?? []);
				const hasDup = dupRows.some(
					(d: any) =>
						d.status === "Pending" || d.status === "Approved"
				);
				if (hasDup) {
					field.setFeedback({
						type: "error",
						messages: [
							`There is already an Approved/Pending case open with the same medication ${medName}`,
						],
					});
				} else {
					field.setFeedback({ type: "error", messages: [] });
				}
			} catch (err) {
				console.error("ChangeCaseStatus: duplicate check error", err);
			}
		}

		// Check case status and set require_next_fill
		const status = form.getFieldState("status")?.value;
		if (status !== "Approved") {
			form.setFieldState("require_next_fill", (s) => {
				s.value = "No";
			});
			return;
		}

		try {
			const dispData = await fetchAPI(
				buildApiUrl(
					`/patient_order_dispense/list?_patient_id=eq:${patientId}&_medication_short_name=eq:${encodeURIComponent(medName)}`
				)
			);
			const dispRows = Array.isArray(dispData)
				? dispData
				: (dispData?.data ?? []);
			form.setFieldState("require_next_fill", (s) => {
				s.value = dispRows.length === 0 ? "Yes" : "No";
			});
		} catch (err) {
			console.error("ChangeCaseStatus: dispense check error", err);
		}
	})();
	return "";
};

// ─── AssignCurrentYear ──────────────────────────────────────────────────────
// Old: DSLFx.Validators.AssignCurrentYear
// Trigger field: sup_year (or related)
// Defaults sup_year to the current year if empty.
export const AssignCurrentYear: FieldHandler = (field, { form }) => {
	const supYear = form.getFieldState("sup_year")?.value;
	if (!supYear) {
		form.setFieldState("sup_year", (s) => {
			s.value = String(new Date().getFullYear());
		});
	}
	return "";
};

// ─── AssignNextYear ─────────────────────────────────────────────────────────
// Old: DSLFx.Validators.AssignNextYear
// Trigger field: renewal_year (or related)
// Defaults renewal_year to next year if empty.
export const AssignNextYear: FieldHandler = (field, { form }) => {
	const renewalYear = form.getFieldState("renewal_year")?.value;
	if (!renewalYear) {
		form.setFieldState("renewal_year", (s) => {
			s.value = String(new Date().getFullYear() + 1);
		});
	}
	return "";
};

// ─── CaseCheckCondition ─────────────────────────────────────────────────────
// Old: DSLFx.Validators.CheckCondition (patient_case version)
// Trigger field: start_copay_app / is_340b_available
// Sets condition_chck = "Yes" when 340B is available and copay is not started.
export const CaseCheckCondition: FieldHandler = (field, { form }) => {
	const startCopayApp = form.getFieldState("start_copay_app")?.value;
	const is340bAvailable = form.getFieldState("is_340b_available")?.value;
	const bridgeDose = form.getFieldState("bridege_dose_required")?.value;

	form.setFieldState("condition_chck", (s) => {
		s.value = "No";
	});

	if (
		is340bAvailable === "Yes" &&
		bridgeDose === "Yes" &&
		(startCopayApp === "No" || startCopayApp == null)
	) {
		form.setFieldState("condition_chck", (s) => {
			s.value = "Yes";
		});
	}
	return "";
};

// ─── CaseCheckForAlert ──────────────────────────────────────────────────────
// Old: DSLFx.Validators.CheckForAlert (patient_case version)
// Trigger field: bridege_dose_required / start_copay_app / start_340b_app
// Sets alert messages for bridge dose requirements based on copay/340B availability.
export const CaseCheckForAlert: FieldHandler = (field, { form }) => {
	form.setFieldState("show_alert", (s) => {
		s.value = "No";
	});

	const bridgeDose = form.getFieldState("bridege_dose_required")?.value;
	if (bridgeDose === "No" || !bridgeDose) return "";

	const startCopayApp = form.getFieldState("start_copay_app")?.value;
	const start340bApp = form.getFieldState("start_340b_app")?.value;
	const isCopayAvailable = form.getFieldState("is_copay_available")?.value;
	const is340bAvailable = form.getFieldState("is_340b_available")?.value;

	if (startCopayApp === "Yes") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value = "Copay will add to the case for bridge dose";
		});
	}

	if (start340bApp === "Yes") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value =
				"340B/discount drug program will add to the case for bridge dose";
		});
	}

	if (
		(startCopayApp === "No" && start340bApp === "No") ||
		(isCopayAvailable === "No" && is340bAvailable === "No")
	) {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value =
				"Drug has no copay or 340B options available, you will need to communicate with the member regarding other options for bridge dose";
		});
	}

	return "";
};

// ─── MissingPhySignDate ─────────────────────────────────────────────────────
// Old: DSLFx.Validators.MissingPhySignDate
// Trigger field: stage
// Sets missing_phy_sign_date to today when stage = "PAP Pending Physician Signature",
// clears it otherwise.
export const MissingPhySignDate: FieldHandler = (field, { form }) => {
	const stage = field.value;

	if (stage === "PAP Pending Physician Signature") {
		form.setFieldState("missing_phy_sign_date", (s) => {
			s.value = dayjs().format("MM/DD/YYYY");
		});
	} else {
		form.setFieldState("missing_phy_sign_date", (s) => {
			s.value = undefined;
		});
	}
	return "";
};
